import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * LoginPage — public route. Shows the single "Continuar con Google" CTA.
 *
 * The login flow redirects the user to auth.emergentagent.com, which handles the
 * Google OAuth dance and sends the user back to `<origin>/#session_id=<token>`.
 * The AuthCallback component then exchanges that session_id via the backend.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function LoginPage() {
  const { user, ready } = useAuth();
  const [starting, setStarting] = useState(false);

  // If the user is already authenticated, send them home.
  useEffect(() => {
    if (ready && user) {
      window.location.replace("/");
    }
  }, [ready, user]);

  const startGoogleLogin = () => {
    setStarting(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="nx-page-bg min-h-screen w-full flex items-center justify-center px-6"
      data-testid="login-page"
    >
      <div
        className="
          relative w-full max-w-[420px] rounded-[28px]
          bg-white/[0.03] backdrop-blur-xl
          border border-[rgba(0,229,255,0.14)]
          shadow-[0_0_60px_-20px_rgba(0,229,255,0.35)]
          px-7 pt-9 pb-8
          flex flex-col items-center
        "
      >
        {/* Ambient cyan glow accent */}
        <div
          aria-hidden="true"
          className="absolute -top-24 left-1/2 -translate-x-1/2 h-48 w-48 rounded-full blur-3xl bg-[rgba(0,229,255,0.18)] pointer-events-none"
        />

        {/* Brand */}
        <p className="text-[10.5px] uppercase tracking-[0.3em] text-[#00E5FF]/80">
          NexAgenda Y
        </p>
        <h1
          className="mt-3 text-center text-[26px] sm:text-[28px] font-semibold leading-tight text-[#E5E7EB]"
          data-testid="login-title"
          aria-label="Tu agenda de élite, Sr. Fernando"
        >
          Tu agenda de élite,{" "}
          <br />
          <span className="text-[#00E5FF] [text-shadow:0_0_18px_rgba(0,229,255,0.35)]">
            Sr. Fernando
          </span>
        </h1>
        <p className="mt-3 text-center text-[13px] text-[#9CA3AF] max-w-[280px] leading-relaxed">
          9 logros diarios. Un copiloto por voz. Métricas que importan.
          Accede con Google para comenzar.
        </p>

        {/* Google CTA */}
        <button
          type="button"
          onClick={startGoogleLogin}
          disabled={starting || (ready && user)}
          data-testid="login-google-button"
          className="
            mt-8 w-full h-12 rounded-full
            flex items-center justify-center gap-3
            bg-[#0F1524] border border-[rgba(0,229,255,0.35)]
            text-[#E5E7EB] text-[14.5px] font-medium
            shadow-[0_0_18px_rgba(0,229,255,0.15)]
            hover:border-[rgba(0,229,255,0.7)] hover:shadow-[0_0_26px_rgba(0,229,255,0.28)]
            transition-all duration-200
            active:scale-[0.985]
            disabled:opacity-60 disabled:cursor-not-allowed
          "
        >
          {/* Google "G" multicolor SVG — official brand marks */}
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path
              fill="#FFC107"
              d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
            />
            <path
              fill="#FF3D00"
              d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
            />
            <path
              fill="#4CAF50"
              d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
            />
            <path
              fill="#1976D2"
              d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.002-.001 6.19 5.238 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
            />
          </svg>
          <span>{starting ? "Redirigiendo…" : "Continuar con Google"}</span>
        </button>

        <p className="mt-5 text-center text-[10.5px] text-[#6B7280] leading-relaxed">
          Al continuar, aceptas la persistencia de tu progreso en esta cuenta.
          <br />
          Puedes cerrar sesión desde tu avatar en cualquier momento.
        </p>
      </div>
    </div>
  );
}
